import React, { useEffect, useState, useMemo } from "react";

type UniformLayout = {
  type: "uniform";
  groups: number;
  itemsPerGroup: number;
  sizeClass: string;
};

type AdvancedLayout = {
  type: "advanced";
  row1Count: number;
  row2Count: number;
  row1Size: string;
  row2Size: string;
};

type Layout = UniformLayout | AdvancedLayout;

const MetricsSkeleton: React.FC = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const layout: Layout = useMemo(() => {
    if (width < 768) {
      return {
        type: "uniform",
        groups: 4,
        itemsPerGroup: 3,
        sizeClass:
          "max-md:h-[170px] max-md:w-[170px] max-sm:h-[120px] max-sm:w-[120px]",
      };
    }

    if (width < 1024) {
      return {
        type: "uniform",
        groups: 3,
        itemsPerGroup: 3,
        sizeClass: "h-[200px] w-[200px]",
      };
    }

    if (width < 1280) {
      return {
        type: "uniform",
        groups: 3,
        itemsPerGroup: 3,
        sizeClass: "h-[210px] w-[210px]",
      };
    }

    if (width < 1536) {
      return {
        type: "advanced",
        row1Count: 6,
        row2Count: 5,
        row1Size: "h-[180px] w-[180px]",
        row2Size: "h-[200px] w-[200px]",
      };
    }

    return {
      type: "advanced",
      row1Count: 6,
      row2Count: 5,
      row1Size: "h-[200px] w-[200px]",
      row2Size: "h-[200px] w-[200px]",
    };
  }, [width]);

  const renderUniformRows = (u: UniformLayout) => {
    return Array.from({ length: u.groups }).map((_, rowIndex) => (
      <div
        key={`row-${rowIndex * 10}`}
        className="flex items-center justify-center gap-5 flex-wrap my-2"
      >
        {Array.from({ length: u.itemsPerGroup }).map((_, itemIndex) => (
          <div
            key={`item-${rowIndex}-${itemIndex}`}
            className={`${u.sizeClass} skeleton !rounded-full`}
          ></div>
        ))}
      </div>
    ));
  };

  const renderAdvancedRows = (a: AdvancedLayout) => {
    return (
      <>
        <div className="flex items-center justify-center gap-5 flex-wrap">
          {Array.from({ length: a.row1Count }).map((_, idx) => (
            <div
              key={`r1-${idx * 10}`}
              className={`${a.row1Size} skeleton !rounded-full`}
            ></div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-5 flex-wrap mt-4">
          {Array.from({ length: a.row2Count }).map((_, idx) => (
            <div
              key={`r2-${idx * 10 * 10}`}
              className={`${a.row2Size} skeleton !rounded-full`}
            ></div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div>
      <div className="h-14 max-sm:h-8 max-sm:w-full w-96 max-md:w-56 skeleton"></div>

      <div className="mb-10 flex flex-col">
        {layout.type === "uniform"
          ? renderUniformRows(layout)
          : renderAdvancedRows(layout)}
      </div>
    </div>
  );
};

export default MetricsSkeleton;
